import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const Editor = ({ t, placeholders, onReload }) => {
  const [titolo, setTitolo] = useState(t.titolo);
  const [contenuto, setContenuto] = useState(t.contenuto);
  const ref = useRef(null);

  useEffect(() => {
    setTitolo(t.titolo);
    setContenuto(t.contenuto);
  }, [t]);

  const insert = (ph) => {
    const el = ref.current;
    const tag = `{${ph}}`;
    const s = el?.selectionStart ?? contenuto.length;
    const e = el?.selectionEnd ?? contenuto.length;
    setContenuto(contenuto.slice(0, s) + tag + contenuto.slice(e));
    setTimeout(() => { el?.focus(); el?.setSelectionRange(s + tag.length, s + tag.length); }, 0);
  };

  const save = async () => {
    try {
      await http.put(`/admin/templates/${t.tipo}`, { titolo, contenuto });
      toast.success("Modello salvato");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare il testo predefinito? Le modifiche andranno perse.")) return;
    try {
      await http.post(`/admin/templates/${t.tipo}/reset`);
      toast.success("Modello ripristinato");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_260px]">
      <div className="space-y-4">
        <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} data-testid="template-title-input" className="h-11 bg-white font-heading text-base font-semibold" />
        <Textarea ref={ref} value={contenuto} onChange={(e) => setContenuto(e.target.value)} data-testid="template-content-textarea" className="min-h-[520px] bg-white font-mono text-[13px] leading-relaxed" />
        <div className="flex flex-wrap gap-2">
          <button onClick={save} data-testid="template-save-button" className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-600"><Save className="h-4 w-4" /> Salva modello</button>
          <button onClick={reset} data-testid="template-reset-button" className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:border-slate-900"><RotateCcw className="h-4 w-4" /> Ripristina predefinito</button>
        </div>
      </div>
      <aside className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Campi automatici</p>
          <p className="mt-1 text-xs text-slate-500">Clicca per inserire nel testo.</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {placeholders.map((ph) => (
              <button key={ph} onClick={() => insert(ph)} data-testid={`placeholder-${ph.replace(/_/g, "-")}`} className="rounded border border-blue-100 bg-blue-50 px-2 py-1 font-mono text-[11px] text-blue-700 transition-colors hover:bg-blue-600 hover:text-white">{`{${ph}}`}</button>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs leading-relaxed text-slate-500">
          Le righe che iniziano con <span className="font-mono text-slate-900">## </span> diventano titoli di sezione e compaiono nell'indice del documento.
        </div>
      </aside>
    </div>
  );
};

export default function Templates() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("privacy_condomini");
  const load = useCallback(() => http.get("/admin/templates").then(({ data }) => setData(data)), []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;
  const t = data.templates.find((x) => x.tipo === active);

  return (
    <div className="fade-up space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">Testi</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-slate-900">Modelli documenti</h1>
        <p className="mt-1 text-sm text-slate-500">Un modello per tipo di documento, compilato automaticamente con i dati di ogni condominio.</p>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {data.templates.map((x) => (
          <button key={x.tipo} onClick={() => setActive(x.tipo)} data-testid={`template-tab-${x.tipo}`} className={`-mb-px shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${active === x.tipo ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {x.codice}
          </button>
        ))}
      </div>
      {t && <Editor t={t} placeholders={data.placeholders} onReload={load} />}
    </div>
  );
}
